<template>
  <nav
    class="dq-mobile-tabbar"
    :style="{ '--dq-mobile-tab-count': navigationItems.length + 1 }"
    aria-label="Primary mobile navigation"
  >
    <router-link
      v-for="item in navigationItems"
      :key="item.label"
      :to="item.to"
      class="dq-mobile-tabbar__item"
      :class="{ 'is-active': isActive(item) }"
      @click="closeDrawer"
    >
      <i :class="item.icon" aria-hidden="true"></i>
      <span>{{ item.label }}</span>
    </router-link>

    <button
      type="button"
      class="dq-mobile-tabbar__item"
      :class="{ 'is-active': drawerOpen }"
      aria-label="Open full navigation"
      :aria-expanded="drawerOpen"
      @click="toggleDrawer"
    >
      <i class="ti ti-menu-2" aria-hidden="true"></i>
      <span>More</span>
    </button>
  </nav>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/stores/authStore";
import { useAccessStore } from "@/stores/accessStore";
import { resolveHomeRoute } from "@/utils/homeRoute";

const route = useRoute();
const auth = useAuthStore();
const access = useAccessStore();
const drawerOpen = ref(false);

const roles = computed(() => auth.user?.roles || []);
const isSuperAdmin = computed(() => roles.value.includes("SuperAdmin"));
const isAdminOrHr = computed(
  () =>
    roles.value.includes("Admin") ||
    roles.value.includes("Hr") ||
    roles.value.includes("HR"),
);
const isPureClient = computed(
  () =>
    roles.value.includes("Client") &&
    !roles.value.includes("Employee") &&
    !isAdminOrHr.value &&
    !isSuperAdmin.value,
);

const navigationItems = computed(() => {
  if (isSuperAdmin.value) {
    return [
      { label: "Home", to: "/super-admin/dashboard", icon: "ti ti-home" },
      {
        label: "Companies",
        to: "/super-admin/companies",
        icon: "ti ti-building-skyscraper",
      },
      {
        label: "Plans",
        to: "/super-admin/subscription",
        icon: "ti ti-credit-card",
      },
      { label: "Alerts", to: "/notifications", icon: "ti ti-bell" },
    ];
  }

  if (isPureClient.value) {
    return [
      { label: "Home", to: resolveHomeRoute(auth), icon: "ti ti-home" },
      {
        label: "Contacts",
        to: "/crm/contacts",
        icon: "ti ti-address-book",
        permission: "Contact.Read",
        module: "crm_contacts",
      },
      {
        label: "Projects",
        to: "/projects/projects-list",
        icon: "ti ti-folders",
        permission: "Project.Read",
        module: "projects",
      },
      {
        label: "Invoices",
        to: "/sales/invoices",
        icon: "ti ti-file-invoice",
        permission: "Invoice.Read",
        module: "fa_sales",
      },
    ].filter(
      (item) =>
        (!item.permission || auth.can?.(item.permission)) &&
        (!item.module || !access.loaded || access.hasModule(item.module)),
    );
  }

  if (isAdminOrHr.value) {
    return [
      { label: "Home", to: resolveHomeRoute(auth), icon: "ti ti-home" },
      {
        label: "People",
        to: "/employee/employee-list",
        icon: "ti ti-users",
        module: "hrm_employee",
      },
      {
        label: "Salaries",
        to: "/payroll/employee-salary",
        icon: "ti ti-cash",
        module: "hrm_payroll",
      },
      {
        label: "Attendance",
        to: "/attendance/attendance-admin",
        icon: "ti ti-calendar-check",
        module: "hrm_attendance",
      },
    ].filter((item) => !item.module || !access.loaded || access.hasModule(item.module));
  }

  return [
    { label: "Home", to: resolveHomeRoute(auth), icon: "ti ti-home" },
    {
      label: "Attendance",
      to: "/attendance/attendance-employee",
      icon: "ti ti-calendar-check",
    },
    {
      label: "Leave",
      to: "/leave/leaves-employee",
      icon: "ti ti-beach",
    },
    { label: "Payslips", to: "/payroll/my-payslips", icon: "ti ti-receipt" },
  ].filter((item) => {
    if (!access.loaded || item.label === "Home") return true;
    if (item.label === "Payslips") return access.hasModule("hrm_payroll");
    return access.hasModule("hrm_attendance");
  });
});

function isActive(item) {
  const target =
    typeof item.to === "string" ? item.to : item.to?.path || "";
  if (!target) return false;
  if (target.endsWith("dashboard")) return route.path === target;
  return route.path === target || route.path.startsWith(`${target}/`);
}

function syncDrawer(event) {
  drawerOpen.value = !!event?.detail?.open;
}

function toggleDrawer() {
  document.body.classList.toggle("slide-nav");
  drawerOpen.value = document.body.classList.contains("slide-nav");
  document.body.classList.toggle("dq-mobile-drawer-open", drawerOpen.value);
  window.dispatchEvent(
    new CustomEvent("dq-mobile-sidebar", {
      detail: { open: drawerOpen.value },
    }),
  );
}

function closeDrawer() {
  document.body.classList.remove("slide-nav", "dq-mobile-drawer-open");
  drawerOpen.value = false;
  window.dispatchEvent(
    new CustomEvent("dq-mobile-sidebar", { detail: { open: false } }),
  );
}

onMounted(() => {
  window.addEventListener("dq-mobile-sidebar", syncDrawer);
  access.ensureAccess?.();
});
onBeforeUnmount(() =>
  window.removeEventListener("dq-mobile-sidebar", syncDrawer),
);
</script>
